import { writable } from "svelte/store";
import { createUserWithEmailAndPassword, signOut, updateEmail, signInWithEmailAndPassword, updatePassword } from "firebase/auth";
import { auth, db } from "$lib/firebase";
import { collection, query, where, getDocs, limit, orderBy, addDoc, getDoc, doc, deleteDoc, Timestamp } from "firebase/firestore";

import type { LemonSquadQuote, OrderData, InfoRequest } from "$lib/types";

export const focusForm = writable(false);

export const orderFormStore = writable({
    carConfirmed: false,
    userConsent: false,
    uuid: "",
    //customer
    c_fname: "",
    c_lname: "",
    c_email: "",
    c_phone: "",
    //order data
    order_type: "", //
    addons: [],
    vehicle_year: 0,
    vehicle_make: "",
    vehicle_model: "",
    dealer_name: "",
    contact_name: "",
    seller_phone1: "",
    vehicle_address1: "",
    vehicle_address2: "",
    vehicle_stock_no: "",
    vehicle_custom_id: "",
    vehicle_email: "",
    vehicle_city: "",
    vehicle_state: "",
    vehicle_zip: "",
    vehicle_vin: "",
    seller_asking_price: "",
    vehicle_notes: "",
    vehicle_mileage: 0,
    lemonSquadPriceInCents: 0,
    certificationPriceInCents: 0,
    isAnnually: false,
    isCertified: true,
    stripe_price_id: "",
    lemonsquad_order_id: "",
    createdAt: null,
    order_id: "",
});

export const validFields = writable({
    vehicle_year: true,
    vehicle_make: true,
    vehicle_model: true,
    c_fname: true,
    c_lname: true,
    c_email: true,
    c_phone: true,
    vehicle_zip: true,
    vehicle_address1: true,
    vehicle_city: true,
    vehicle_state: true,
    contact_name: true,
    seller_phone1: true,
});
export const formSummaryStep = writable({
    checkoutSummary: false,
});

export const stepperStore = writable({
    currentStep: 1,
    step_one: false, //make, model, year,
    step_two: false, // Vehicle Address and Contact
    step_three: false, // user contact
    step_four: false, // pricing and checkout
});

export const orderFormIsSubmitting = writable(false);

export const lemonSquadPricingStore = writable({
    //todo i should really add this to the database and
    // //have a function taht checks for updates via the order_type api call
    standard: 219.99,
    classic: 329.99,
    exotic: 285.99,
});

export const certificationPricingStore = {
    //todo: Make the lemonsquad api call only update at the server level via a cron job or something
    2: {
        type: "standard",
        stripe_id_monthly: "price_1Q856fH9AZhgtSOrqZ7XXsDZ",
        stripe_id_annual: "price_1Q84xRH9AZhgtSOrLXy13Xrr",
        cents_monthly: 5999,
        cents_annually: 67999,
        lemonPrice: 21999,
        sport: {
            //if sport is selected set everything to using the .sport child object
            type: "standard",
            stripe_id_monthly: "price_1Q858pH9AZhgtSOrpp2cdimK",
            stripe_id_annual: "price_1Q7ldrH9AZhgtSOrsehKbymA",
            cents_monthly: 9499,
            cents_annually: 105999,
            lemonPrice: 21999,
        },
    },
    3: {
        type: "classic",
        stripe_id_monthly: "price_1Q85DKH9AZhgtSOrZwi6gxSX",
        stripe_id_annual: "price_1Q85DsH9AZhgtSOrL3PZXlRn",
        cents_monthly: 9999,
        cents_annually: 109999,
        lemonPrice: 32999,
        sport: {
            //if sport is selected set everything to using the .sport child object
            type: "classic",
            stripe_id_monthly: "price_1Q858pH9AZhgtSOrpp2cdimK",
            stripe_id_annual: "price_1Q85I6H9AZhgtSOrR9ynAUs0",
            cents_monthly: 12999,
            cents_annually: 143999,
            lemonPrice: 21999,
        },
    },
    4: {
        type: "exotic",
        stripe_id_monthly: "price_1Q85L2H9AZhgtSOrIDkJw8bm",
        stripe_id_annual: "price_1Q85LRH9AZhgtSOrDxybQX32",
        cents_monthly: 17999,
        cents_annually: 197999,
        lemonPrice: 28599,
    },
};
export const referralHandlers = async () => {
    getPriceByType: (type: string) => {
        switch (type) {
        }
    };
};

export const certificationPricingStore_DEV = {
    //todo: Make the lemonsquad api call only update at the server level via a cron job or something
    2: {
        type: "standard",
        stripe_id_quarterly: "price_1Q7jYxH9AZhgtSOriheU35tF",
        stripe_id_annual: "price_1Q7T1bH9AZhgtSOr1LzJRIQK",
        cents_monthly: 17997,
        cents_annually: 71988,
        lemonPrice: 21999,
    },
    3: {
        type: "classic",
        stripe_id_quarterly: "price_1P6zzhH9AZhgtSOr5P1eQbTw",
        stripe_id_annual: "price_1P6zqdH9AZhgtSOrgjd0Z07A",
        cents_monthly: 21249.75,
        cents_annually: 84999,
        lemonPrice: 32999,
    },
    4: {
        type: "exotic",
        stripe_id_quarterly: "price_1P706CH9AZhgtSOrrBsSnVjt",
        stripe_id_annual: "price_1P705dH9AZhgtSOrYGnundOu",
        cents_monthly: 47399,
        cents_annually: 189599,
        lemonPrice: 28599,
    },
};

export const orderPricingStore = writable({
    loading: false, //refers to if there is already a request in progress
    currentQuoteData: {
        type: "",
        addons: "",
        vehicle_address1: "",
        vehicle_city: "",
        vehicle_state: "",
        vehicle_zip: "",
    } as LemonSquadQuote,
    lemonSquadPriceInCents: 0,
    certificationPriceInCents: 0,
});

export const authStore = writable({
    isLoading: true,
    currentUser: auth.currentUser || null,
    data: {},
});

export const authHandlers = {
    signUp: async (email: string, password: string) => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        return userCredential;
        // const user = auth.currentUser;
        // if (user) {
        //      TODO
        //      await db.collection("members").doc(user.uid).set({
        //         email: user.email
        //      });
        // }
    },
    logout: async () => {
        await signOut(auth).then(() => {
            authStore.set({ isLoading: false, currentUser: null, data: {} });
        });
    },
    updatePassword: async (newPassword: string) => {
        if (auth.currentUser && newPassword) {
            await updatePassword(auth.currentUser, newPassword);
        }
    },
    updateEmail: async (newEmail: string) => {
        if (auth.currentUser && newEmail) {
            await updateEmail(auth.currentUser, newEmail);
        }
    },
    signIn: async (email: string, password: string) => {
        await signInWithEmailAndPassword(auth, email, password);
    },
    getStripeCustomerByEmail: async (email: string) => {
        const usersRef = await collection(db, "stripeCustomers", email);

        const ordersRef = await collection(db, "stripeOrders", email, "orders");
        // get the last order by timestamp

        const q = query(usersRef, where("c_email", "==", email));

        const querySnapshot = await getDocs(q);

        console.log("getUserByEmail querySnapshot: ", querySnapshot);
        console.log("type of querySnapshot: ", typeof querySnapshot);
        return querySnapshot[0]; //return the first document
    },
    getStripeCustomerOrders: async (email: string) => {
        const usersRef = await collection(db, "stripeCustomers", email);
        const q = query(usersRef, where("c_email", "==", email));
        const querySnapshot = await getDocs(q);
        return querySnapshot;
    },
    getStripeCustomerSubscriptions: async (email: string) => {
        const usersRef = await collection(db, "stripeCustomers", email);
        const q = query(usersRef, where("c_email", "==", email));
        const querySnapshot = await getDocs(q);
        return querySnapshot;
    },
    // given an email, copy all the data into a member document in the members collection
    // then delete the stripe customer
    convertStripeCustomerToMember: async (email: string) => {
        const usersRef = await doc(db, "stripeCustomers", email);
        const userdoc = await getDoc(usersRef);
        if (!userdoc.exists()) {
            return `error finding member with email: ${email}`;
        }
        console.log("UserDoc: ", userdoc.data());
        const userData = { ...userdoc.data(), isActive: true };
        const memberRef = await collection(db, "members", email);
        const memberRefSnapshot = await addDoc(memberRef, userData);
        if (!memberRefSnapshot) {
            return `error converting member with email: ${email}`;
        }
        //add orders to member
        const ordersRef = await collection(db, "stripeCustomers", email, "orders");
        const orderSnapshot = await getDocs(ordersRef);
        const ordersData = orderSnapshot.docs.map((doc) => doc.data());
        for (let order of ordersData) {
            const orderRef = await collection(db, "members", email, "orders");
            const orderRefSnapshot = await addDoc(orderRef, order);
            if (!orderRefSnapshot) {
                console.log("error adding order to member doc: ", email);
            }
        }
        for (let order of ordersData) {
            deleteDoc(order.ref);
        }
        //add subscriptions to member
        const subscriptionsRef = await collection(db, "stripeCustomers", email, "subscriptions");
        const subscriptionSnapshot = await getDocs(subscriptionsRef);
        const subscriptionsData = subscriptionSnapshot.docs.map((doc) => doc.data());
        for (let subscription of subscriptionsData) {
            const subscriptionRef = await collection(db, "members", email, "subscriptions");
            const subscriptionRefSnapshot = await addDoc(subscriptionRef, subscription);
            if (!subscriptionRefSnapshot) {
                console.log("error adding subscription to member doc: ", email);
            }
        }
        for (let subscription of subscriptionsData) {
            deleteDoc(subscription.ref);
        }

        console.log("Successfully converted StripeCustomer to Member with id: ", memberRefSnapshot.id);
        return memberRefSnapshot.id;
    },
};

export const cartUtils = {
    getCertPriceByType: (type: string) => {
        //FOR TEST MODE
        // CHANGE TO TRUE TO USE DEV PRICING STORE
        const isTestmode = false; // true is test mode and will using the dev pricing store

        let priceStore;
        if (isTestmode) {
            priceStore = certificationPricingStore_DEV[type];
        } else {
            priceStore = certificationPricingStore[type];
        }
        return priceStore;
    },
};
export const infoRequestStore: any = writable({
    c_email: "",
    c_name: "",
    c_phone: "",
    message: "",
    order_id: "",
    consent: true,
});
export const contactFormStore = writable({
    c_email: "",
    c_fname: "",
    c_lname: "",
    c_phone: "",
    message: "",
    consent: false,
    timeStamp: Timestamp.now(),
});
